import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Download, Trash2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { ConfirmButton } from "@/components/common/ConfirmButton";

export default function Tables() {
  const { restaurant } = useAuth();
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get("/restaurant/tables").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const wrap = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  const urlFor = (t) => `${window.location.origin}/r/${restaurant.slug}?table=${t.id}`;
  const download = (t) => {
    const c = document.getElementById(`qr-${t.id}`);
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `${restaurant.slug}-${t.name}.png`;
    a.click();
  };

  if (!items) return <Loader />;
  return (
    <div>
      <PageHeader title="Tables & QR codes" subtitle="Print a QR for each table. Scanning opens your menu with the table pre-selected." />
      <form onSubmit={(e) => { e.preventDefault(); wrap(() => api.post("/restaurant/tables", { name, is_active: true }), "Table added").then(() => setName("")); }} className="mb-6 flex gap-2">
        <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Table 5" className="max-w-xs" data-testid="table-name-input" />
        <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="table-add-btn"><Plus className="mr-1 h-4 w-4" />Add</Button>
      </form>

      <div className="mb-6 flex flex-col items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center">
        <QRCodeCanvas id="qr-general" value={`${window.location.origin}/r/${restaurant.slug}`} size={96} />
        <div>
          <p className="font-heading font-semibold text-slate-900">General menu QR</p>
          <p className="text-xs text-slate-500 break-all">{window.location.origin}/r/{restaurant.slug}</p>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => download({ id: "general", name: "menu" })} data-testid="qr-download-general"><Download className="mr-1 h-4 w-4" />Download</Button>
        </div>
      </div>

      {items.length === 0 ? <EmptyState icon={QrCode} title="No tables yet" description="Add tables to generate table-specific QR codes." /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tables-list">
          {items.map((t) => (
            <div key={t.id} className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-5" data-testid={`table-card-${t.id}`}>
              <QRCodeCanvas id={`qr-${t.id}`} value={urlFor(t)} size={140} includeMargin className={t.is_active ? "" : "opacity-30"} />
              <p className="mt-2 font-heading font-semibold text-slate-900">{t.name}</p>
              <div className="mt-3 flex items-center gap-2">
                <Switch checked={t.is_active} onCheckedChange={(v) => wrap(() => api.patch(`/restaurant/tables/${t.id}`, { name: t.name, is_active: v }))} data-testid={`table-active-${t.id}`} />
                <Button size="sm" variant="outline" onClick={() => download(t)} data-testid={`table-download-${t.id}`}><Download className="h-4 w-4" /></Button>
                <ConfirmButton title={`Delete ${t.name}?`} description="Its QR code will stop working." onConfirm={() => wrap(() => api.delete(`/restaurant/tables/${t.id}`), "Deleted")} className="rounded-md p-2 hover:bg-slate-100" testId={`table-delete-${t.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></ConfirmButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
